import { useMemo } from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  isEndedDisplayStatus,
  toStudyDisplayStatus
} from '../../common/studyParticipantDisplayStatus';
import {
  StudyParticipant,
  StudyParticipantStatus
} from '../../models/StudyParticipant';
import { StudyStatus } from '../../models/Study';
import { ParticipantEventOccurrence } from '../../screens/study/helpers/ParticipantEventHelpers';

export function useHasActiveStudy() {
  const { studyParticipants, allOccurrences } = useUserContext();

  return useMemo(
    () => hasActiveStudy(studyParticipants ?? [], allOccurrences),
    [allOccurrences, studyParticipants]
  );
}

// Eligible = the server has not ended it, it is not paused or disqualified, and the study is open.
export const isEligibleStudyParticipant = (
  studyParticipant: StudyParticipant
) =>
  !isEndedDisplayStatus(toStudyDisplayStatus(studyParticipant)) &&
  !studyParticipant.paused &&
  !studyParticipant.disqualified &&
  studyParticipant.study?.status !== StudyStatus.Completed &&
  studyParticipant.study?.status !== StudyStatus.Locked;

// A study can only be started while Testing or Active, Recruiting.
export const isJoinableStudyParticipant = (
  studyParticipant?: StudyParticipant
) =>
  studyParticipant?.study?.status === StudyStatus.Testing ||
  studyParticipant?.study?.status === StudyStatus.ActiveRecruiting;

const lastCompletedActivityDate = (studyParticipant: StudyParticipant) => {
  let latest: Date | undefined;
  for (const participantEvent of studyParticipant.participantEvents || []) {
    for (const log of participantEvent.participantEventLogs || []) {
      if (log.completedDate && (!latest || log.completedDate > latest)) {
        latest = log.completedDate;
      }
    }
  }
  return latest;
};

// The participant a caregiver worked on last, or undefined when none of them
// has a completed form. Ties keep the incoming list order.
const selectMostRecentlyActiveParticipant = (
  studyParticipants: StudyParticipant[]
) => {
  let selected: StudyParticipant | undefined;
  let selectedDate: Date | undefined;
  for (const studyParticipant of studyParticipants) {
    const completedDate = lastCompletedActivityDate(studyParticipant);
    if (completedDate && (!selectedDate || completedDate > selectedDate)) {
      selected = studyParticipant;
      selectedDate = completedDate;
    }
  }
  return selected;
};

// Enrollments the Today header can switch between: every eligible one while none has started, otherwise a caregiver's pediatric ones.
export const selectableStudyParticipants = (
  studyParticipants: StudyParticipant[]
) => {
  const eligible = (studyParticipants || []).filter(isEligibleStudyParticipant);
  return eligible.every(sp => !sp.startDate)
    ? eligible
    : eligible.filter(sp => Boolean(sp.study?.isPediatric));
};

const enrollmentRank = (studyParticipant: StudyParticipant) =>
  studyParticipant.status === StudyParticipantStatus.Enrolled
    ? 0
    : studyParticipant.status === StudyParticipantStatus.Consented
      ? 1
      : 2;

// Single prioritized pick shared across the app so selections can't diverge:
// Enrolled before Consented before the rest, then within that the most recently
// completed form, then a started study, then a joinable one, then any eligible study.
export const selectPrioritizedStudyParticipant = (
  studyParticipants: StudyParticipant[]
) => {
  const eligible = (studyParticipants || []).filter(isEligibleStudyParticipant);
  const topRank = Math.min(...eligible.map(enrollmentRank));
  const candidates = eligible.filter(sp => enrollmentRank(sp) === topRank);
  return (
    selectMostRecentlyActiveParticipant(candidates) ??
    candidates.find(sp => !!sp.startDate) ??
    candidates.find(isJoinableStudyParticipant) ??
    candidates[0]
  );
};

export const gActiveStudyParticipant = (
  studyParticipants: StudyParticipant[],
  allOccurrences: ParticipantEventOccurrence[]
) => {
  const studyParticipant = selectPrioritizedStudyParticipant(studyParticipants);
  if (allOccurrences.length > 0) {
    return studyParticipant;
  } else {
    return undefined;
  }
};

export const hasActiveStudy = (
  studyParticipants: StudyParticipant[],
  allOccurrences: ParticipantEventOccurrence[]
) => {
  const studyParticipant = gActiveStudyParticipant(
    studyParticipants,
    allOccurrences
  );
  return studyParticipant && allOccurrences.length > 0;
};
